import { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication, useChatMessage, useMentions } from '@connect-x-app/hybrid-state';
import { COLORS } from '@connect-x-app/shared-ui';
import {
  APPLE_ONLY_FILE_TYPES,
  getValidExtensionsString,
  hasPermissionForFile,
  MAX_ALLOWED_MESSAGE_CHARS,
  MAX_ATTACHMENT_SIZE,
  toBase64,
} from '@connect-x-app/shared-utils';
import { useApp } from '@connect-x-app/web-state';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button, Icon, Modal, ModalOpener, Tooltip } from '@connect-x-app/web-ui';
import { isMacOS, isValidFileMimeType } from '@connect-x-app/web-utils';
import { ReferenceType } from '@floating-ui/react';
import convert from 'heic-convert/browser';
import prettyBytes from 'pretty-bytes';

import { MentionInput, MentionInputRef, QuickReplies } from '../../components';
import { SendMessageModal } from '../sendMessageModal';

import './inputToolbar.scss';

type Props = {
  onSend: (message: string, attachment?: File | null) => void;
};

export const InputToolbar: FC<Props> = ({ onSend }) => {
  const inputFile = useRef<HTMLInputElement | null>(null);
  const inputMentionRef = useRef<MentionInputRef>(null);

  const { t } = useTranslation();
  const { getUsedMentions, shouldShowExternalMentionModal } = useMentions();
  const { canUploadAttachments, user } = useAuthentication();
  const { attachmentPreview, setAttachmentPreview } = useApp();
  const [attachment, setAttachment] = useState<File | null>(null);
  const { message, quickReplies, setMessage } = useChatMessage();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState<ReferenceType | null>(null);

  const validExtensionsString = useMemo(() => {
    return getValidExtensionsString(!isMacOS() ? APPLE_ONLY_FILE_TYPES : [], user?.permissions);
  }, [user?.permissions]);

  const onPressQuickReply = (item: string) => {
    inputMentionRef.current?.sendText(item);
  };

  const amountOfCharsExceed = useMemo(() => message.length - MAX_ALLOWED_MESSAGE_CHARS, [message.length]);

  const onSubmit = () => {
    const usedMentions = getUsedMentions(message);
    if (!isModalVisible && shouldShowExternalMentionModal(usedMentions)) {
      setIsModalVisible(true);
      ModalOpener.instance.open({
        render: () => (
          <SendMessageModal
            message={t('CHAT.MENTIONS.MODAL.MESSAGE')}
            onClose={() => {
              setIsModalVisible(false);
              ModalOpener.instance.close();
            }}
            onConfirm={sendMessage}
            title={t('CHAT.MENTIONS.MODAL.TITLE')}
          />
        ),
      });
      return;
    }
    sendMessage();
  };

  const sendMessage = () => {
    setIsModalVisible(false);
    ModalOpener.instance.close();
    onSend(message, attachmentPreview ? attachment : null);
    setMessage('');
    setAttachment(null);
    setAttachmentPreview('');
  };

  const showAttachmentErrorModal = (title: string, message: string) => {
    ModalOpener.instance.open({
      render: () => (
        <Modal>
          <Modal.Header>{title}</Modal.Header>
          <Modal.Content>{message}</Modal.Content>
          <Modal.Buttons>
            <Button onClick={() => ModalOpener.instance.close()} theme="primary">
              {t('SHARED.BUTTONS.OK')}
            </Button>
          </Modal.Buttons>
        </Modal>
      ),
    });
  };

  const isFileSizeValid = (file: File): boolean => {
    return file.size <= MAX_ATTACHMENT_SIZE;
  };

  const showNotAllowedFileTypeError = () => {
    showAttachmentErrorModal(t('CHAT.INPUT.ALERT.INVALID_FILE_TYPE.TITLE'), t('ERRORS.PERMISSION_DENIED'));
  };

  const showInvalidFileTypeError = () => {
    showAttachmentErrorModal(
      t('CHAT.INPUT.ALERT.INVALID_IMAGE_TYPE.TITLE'),
      t('CHAT.INPUT.ALERT.INVALID_IMAGE_TYPE.DESCRIPTION', { allowedTypes: validExtensionsString }),
    );
  };

  const showFileTooLargeError = () => {
    showAttachmentErrorModal(
      t('CHAT.INPUT.ALERT.ATTACHMENT_TOO_LARGE.TITLE'),
      t('CHAT.INPUT.ALERT.ATTACHMENT_TOO_LARGE.DESCRIPTION', {
        maxSize: prettyBytes(MAX_ATTACHMENT_SIZE, {
          binary: true,
          space: false,
        }),
      }).replace('MiB', 'MB'),
    );
  };

  const processHeicOrHeifImage = async (file: File) => {
    const arrayBuffer = await file.arrayBuffer();
    try {
      const buffer = new Uint8Array(arrayBuffer);
      const outputBuffer = await convert({
        buffer,
        format: 'JPEG',
        quality: 0.5,
      });
      const convertedFile = new File([new Blob([outputBuffer], { type: 'image/jpeg' })], file.name.replace(/\.\w+$/, '.jpeg'), {
        type: 'image/jpeg',
      });
      const convertedPreview = URL.createObjectURL(convertedFile);
      setAttachment(convertedFile);
      setAttachmentPreview(convertedPreview);
    } catch {
      showAttachmentErrorModal(t('CHAT.INPUT.ALERT.UNKNOWN_ERROR.TITLE'), t('CHAT.INPUT.ALERT.UNKNOWN_ERROR.DESCRIPTION'));
    }
  };

  const processStandardFile = async (file: File) => {
    const base64Preview = await toBase64(file);
    setAttachment(file);
    setAttachmentPreview(base64Preview);
  };

  const isHeicOrHeifImage = (file: File): boolean => {
    return file.type === 'image/heic' || file.type === 'image/heif';
  };

  const handlePickAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && !isFileSizeValid(file)) {
      showFileTooLargeError();
    } else if (file && !hasPermissionForFile(file.type, user?.permissions ?? [])) {
      showNotAllowedFileTypeError();
    } else if (file && !(await isValidFileMimeType(file))) {
      showInvalidFileTypeError();
    } else if (file) {
      if (isHeicOrHeifImage(file)) {
        await processHeicOrHeifImage(file);
      } else {
        await processStandardFile(file);
      }
    }

    event.target.value = '';
  };

  const sendDisabled = useMemo(
    () => (!message?.trim() && !attachmentPreview) || amountOfCharsExceed > 0,
    [message, attachmentPreview, amountOfCharsExceed],
  );

  const openPicker = () => {
    inputFile.current?.click();
  };

  return (
    <div className="chat-input-toolbar-container">
      <QuickReplies onPressQuickReply={onPressQuickReply} quickReplies={quickReplies} />
      <div className="input-container">
        <MentionInput
          icon={
            <div className={'open-attachment-picker-button'}>
              {canUploadAttachments ? (
                <Icon colorName={COLORS.zulu.$08} name="circularPlusOutline" onClick={openPicker} size={2.5} />
              ) : null}
            </div>
          }
          message={message}
          ref={inputMentionRef}
          sendDisabled={sendDisabled}
          sendMessage={onSubmit}
          setMessage={setMessage}
        />
        {amountOfCharsExceed > 0 ? (
          <>
            <Tooltip alignment="top" anchor={tooltipAnchor}>
              <slot name="content">
                <span>{t('CHAT.INPUT.TOOLTIP.MAX_AMOUNT_CHARS', { amount: amountOfCharsExceed })}</span>
              </slot>
            </Tooltip>
            <div
              className="max-amount-chars-exceeded"
              onMouseEnter={anchor => setTooltipAnchor(anchor.currentTarget)}
              onMouseLeave={() => setTooltipAnchor(null)}
              role="presentation"
            >
              -{amountOfCharsExceed}
            </div>
          </>
        ) : null}
        <Button className="send-button" disabled={sendDisabled} onClick={onSubmit}>
          <Icon name="paperplaneOutline" size={3} />
        </Button>
      </div>
      <input
        accept={validExtensionsString}
        data-test={CHANNEL_TEST_IDS.input.uploadFile}
        id="file"
        onChange={handlePickAttachment}
        ref={inputFile}
        style={{ display: 'none' }}
        type="file"
      />
    </div>
  );
};
